// @flow
import { Eye } from '@phosphor-icons/react'
import { Button, type ButtonProps } from 'antd'
import { FormBuilderContext } from 'pages/Contexts/FormBuilderContext'
import * as React from 'react'
import { useHistory, useParams } from 'react-router-dom'

import Tooltip from '@/ui/atoms/Tooltip'
import cn from '@/utils/tailwind/cn'

type Props = {
  handleBuild?: () => void,
  handleSave: () => void | Promise<void>,
  handlePreview: () => void,
  handleSettings?: () => void,
  previewDisabled: boolean,
  resourceType?: 'form' | 'document',
  saveLoading: boolean,
  hidePreview?: boolean,
}

const NavButton = ({ className, ...props }: ButtonProps) => (
  <Button
    className={cn(
      'h-10 text-sm !text-gray-700 !outline-none hover:!bg-gray-200/40',
      className
    )}
    type="link"
    {...props}
  />
)

function BuilderNavBar({
  handleBuild,
  handleSave,
  handlePreview,
  handleSettings,
  previewDisabled,
  resourceType = 'form',
  saveLoading,
  hidePreview = false,
}: Props): React.Node {
  const history = useHistory()
  const params = useParams()

  const isPacket = history.location.pathname.includes('/packets')
  const isDocument = history.location.pathname.includes('/pdf')

  const { isDirty: isFormDirty } = React.useContext(FormBuilderContext)

  const isDirty = isPacket || isDocument || isFormDirty

  const packetId = params.packetId

  const baseUrl =
    resourceType === 'document'
      ? `/pdf/${params.resourceId}/edit`
      : `/forms/${params.resourceId}`

  const isNewForm = history.location.pathname.includes('/forms/new')
  const isBuildingActive = history.location.pathname === baseUrl
  const isSettingsActive = history.location.pathname.includes('settings')
  const isDesignActive = history.location.pathname.includes('design')

  const navigateToForm = () => {
    if (handleBuild) {
      handleBuild()
      return
    }

    history.push(baseUrl)
  }

  const navigateToSettings = () => {
    if (packetId) {
      history.push(`/packets/${packetId}/settings/notifications`)
      return
    }
    history.push(`${baseUrl}/settings/general`)
  }

  const navigateToDesign = () => {
    if (packetId) {
      history.push(`/packets/${packetId}/design`)
      return
    }
    history.push(`${baseUrl}/design`)
  }

  const resourceId = params.resourceId || params.packetId

  return (
    <div className="flex h-14 w-full justify-between border-b border-gray-200 bg-white p-2">
      <div className="flex gap-1.5">
        <NavButton
          className={cn((isBuildingActive || isNewForm) && '!bg-gray-200/80')}
          disabled={isBuildingActive || isNewForm}
          onClick={navigateToForm}
        >
          Build
        </NavButton>
        <NavButton
          className={cn(isSettingsActive && '!bg-gray-200/80')}
          disabled={!resourceId}
          onClick={navigateToSettings}
          type="link"
        >
          Settings
        </NavButton>
        {!packetId ? (
          <NavButton
            className={cn(isDesignActive && '!bg-gray-200/80')}
            disabled={!resourceId}
            onClick={navigateToDesign}
            type="link"
          >
            Design
          </NavButton>
        ) : null}
      </div>
      <div className="flex gap-2">
        {hidePreview ? null : (
          <Tooltip
            placement="left"
            title={isDirty && !isDocument ? 'Save to Preview' : ''}
          >
            <NavButton
              className={cn(
                isDirty && !isDocument && 'bg-gray-200/30 !text-gray-500/80'
              )}
              disabled={(previewDisabled || isDirty) && !isDocument}
              onClick={handlePreview}
              type="link"
            >
              <Eye className="text-lg" weight="bold" /> Preview
            </NavButton>
          </Tooltip>
        )}
        <Tooltip
          placement="bottomRight"
          title={!isDirty ? 'Change something to save' : ''}
        >
          <Button
            className="h-10 disabled:!bg-primary disabled:text-white disabled:opacity-50"
            data-cy="save-form"
            disabled={!isDirty}
            loading={saveLoading}
            onClick={handleSave}
            type="primary"
          >
            Save
          </Button>
        </Tooltip>
      </div>
    </div>
  )
}

export default BuilderNavBar
